import { put, call, getContext } from 'redux-saga/effects';
import omit from 'lodash/omit';
import snakeCase from 'lodash/snakeCase';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { createSelector } from 'reselect';

export const createHttpInitState = (namespace, method = 'get', initData = null) => {
  const propNamespace = convertToPropertyName(namespace);
  const methodNamespace = getMethodPropNamespace(method);

  return {
    [propNamespace]: {
      [methodNamespace]: {
        response: initData,
        errors: [],
        pending: false,
      },
    },
  };
};

export const createHttpActions = (namespace, method = 'get') => {
  const actionNamespace = convertToActionName(namespace);
  const methodNamespace = getMethodActionNamespace(method);

  return {
    request: `${actionNamespace}${methodNamespace}`,
    failure: `${actionNamespace}${methodNamespace}_FAILURE`,
    complete: `${actionNamespace}${methodNamespace}_COMPLETE`,
    reset: `${actionNamespace}${methodNamespace}_RESET`,
  };
};

export const createHttpReducers = (namespace, method = 'get') => {
  const propNamespace = convertToPropertyName(namespace);
  const propMethodNamespace = getMethodPropNamespace(method);
  const { request, reset, failure, complete } = createHttpActions(namespace, method);
  const initState = createHttpInitState(namespace, method);

  return {
    [request]: state => ({
      ...state,
      [propNamespace]: {
        ...state[propNamespace],
        [propMethodNamespace]: {
          ...state[propNamespace][propMethodNamespace],
          pending: true,
        },
      },
    }),
    [failure]: (state, { payload: error }) => ({
      ...state,
      [propNamespace]: {
        ...state[propNamespace],
        [propMethodNamespace]: {
          ...state[propNamespace][propMethodNamespace],
          errors: [error],
          pending: false,
        },
      },
    }),
    [complete]: (state, { payload: data }) => ({
      ...state,
      [propNamespace]: {
        ...state[propNamespace],
        [propMethodNamespace]: {
          ...state[propNamespace][propMethodNamespace],
          response: data,
          errors: [],
          pending: false,
        },
      },
    }),
    [reset]: state => ({
      ...state,
      [namespace]: {
        ...state[namespace],
        [method]: initState[namespace][method],
      },
    }),
  };
};

/**
 * Create actions in fixed convention. For namespace "sampleItem", following actions will be created according to the methods:
 * - get: getSampleItemFetch
 * - put: getSampleItemUpdate
 * - post: getSampleItemCreate
 * - delete: getSampleItemDelete
 *
 * The selection with deliver object with following props:
 * { response: Object, pending: Boolean, errors: Array }
 *
 * @param {String} namespace
 * @param {String} method Enum: get, post, put, delete
 * @param {function} parentSelector
 * @returns {Object} Actions
 */
export const createHttpSelectors = (namespace, method = 'get', parentSelector = state => state) => {
  const propNamespace = convertToPropertyName(namespace);
  const propMethodNamespace = getMethodPropNamespace(method);

  return {
    [`get${upperFirst(propNamespace)}${upperFirst(propMethodNamespace)}`]: createSelector(
      parentSelector,
      state => state[propNamespace][propMethodNamespace]
    ),
  };
};

export function createHttpSaga(namespace, method = 'get', logger, urlGetter, dataGetter = () => {}) {
  const { failure, complete } = createHttpActions(namespace, method);

  return function* httpSage({ payload }) {
    try {
      const http = yield getContext('http');

      const requestUrl = urlGetter(payload);
      const data = dataGetter(payload);

      logger.debug('Request', method.toUpperCase(), requestUrl, data);
      const response = yield call(http[method.toLowerCase()], requestUrl, omit(data, 'owner'));

      if (response.status < 200 || response.status > 299) {
        logger.error('Error HTTP status', response.status, response);
        yield put({ type: failure, payload: response.data });
      } else {
        yield put({ type: complete, payload: response.data });
      }
    } catch (e) {
      logger.error('Exception occurred', e);
      yield put({ type: failure, payload: e });
    }
  };
}

function convertToActionName(name) {
  return snakeCase(name).toUpperCase();
}

function convertToPropertyName(name) {
  return camelCase(name);
}

function getMethodPropNamespace(method) {
  return (
    {
      get: 'fetch',
      post: 'create',
      put: 'update',
      delete: 'delete',
    }[method.toLowerCase()] || ''
  );
}

function getMethodActionNamespace(method) {
  return (
    {
      get: '_FETCH',
      post: '_CREATE',
      put: '_UPDATE',
      delete: '_DELETE',
    }[method.toLowerCase()] || ''
  );
}
